import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { NumericFormat } from 'react-number-format';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';

interface PriceTrackerProps {
  cardData: any;
}

interface EbayMarketData {
  avgSold: number;
  minSold: number;
  maxSold: number;
  soldCount: number;
  currency: string;
}

interface MarketApiResponse {
  card_id: number;
  searchTitle: string;
  ebay: EbayMarketData | null;
  lastUpdated: string;
}

const PriceTracker: React.FC<PriceTrackerProps> = ({ cardData }) => {
  // ---- Hooks must be at the top level (no early returns before this) ----
  const [marketData, setMarketData] = useState<MarketApiResponse | null>(null);
  const [loadingMarket, setLoadingMarket] = useState(false);
  const [marketError, setMarketError] = useState<string | null>(null);

  // Fetch eBay market data from backend
  useEffect(() => {
    if (!cardData?.card_id) return;

    let cancelled = false;
    setLoadingMarket(true);
    setMarketError(null);

    axios
      .get<MarketApiResponse>(`/card/${cardData.card_id}/market`)
      .then((res) => {
        if (cancelled) return;
        setMarketData(res.data);
      })
      .catch((err) => {
        console.error('Error loading market data:', err);
        if (cancelled) return;
        setMarketError('Unable to load eBay sold data right now.');
      })
      .finally(() => {
        if (!cancelled) setLoadingMarket(false);
      });

    return () => {
      cancelled = true;
    };
  }, [cardData?.card_id]);

  // ---- After hooks, we can safely bail if no cardData ----
  if (!cardData) return null;

  const totalCost = cardData.card_total_cost || 0;
  const boughtPrice = cardData.card_price || 0;
  const lastSoldPrice = cardData.card_sold_price || null;

  // Simple eBay-style title for manual searches:
  const title = `${cardData.card_set_year} ${cardData.card_game} ${cardData.card_pokemon} ${cardData.card_language} ${cardData.card_number}`.trim();

  // Suggested list price & profit ideas
  const suggestedListPrice = totalCost ? Number((totalCost * 1.25).toFixed(2)) : null; // 25% markup
  const floorListPrice = totalCost ? Number((totalCost * 1.15).toFixed(2)) : null;     // 15% markup

  const profitAtSuggested =
    suggestedListPrice && totalCost ? suggestedListPrice - totalCost : null;

  const roiAtSuggested =
    profitAtSuggested && totalCost
      ? Number(((profitAtSuggested / totalCost) * 100).toFixed(1))
      : null;

  const encodedTitle = encodeURIComponent(title);

  const ebaySearchUrl = `https://www.ebay.com/sch/i.html?_nkw=${encodedTitle}`;
  const tcgPlayerSearchUrl = `https://www.tcgplayer.com/search/all/product?q=${encodedTitle}&view=grid`;
  const point130SearchUrl = `https://www.130point.com/sales/search?s=${encodedTitle}`;

  // Under / over market vs eBay avg sold
  let diffFromEbayAvg: number | null = null;
  let diffPctFromEbayAvg: number | null = null;

  if (marketData?.ebay && totalCost > 0) {
    diffFromEbayAvg = Number(
      (marketData.ebay.avgSold - totalCost).toFixed(2)
    );
    diffPctFromEbayAvg = Number(
      (((marketData.ebay.avgSold - totalCost) / totalCost) * 100).toFixed(1)
    );
  }

  return (
    <Container>
      <Row className="d-flex justify-content-center">
        <Col xs={12} md={10} lg={8} className="custom-info-box shadow-sm">
          <Row>
            <div className="custom-info-header price-tracker-header">
              Price Tracker (eBay Beta)
            </div>
          </Row>

          {/* Cost basis / internal numbers */}
          <Row className="mt-2">
            <Col xs={5}>
              <div className="custom-info-title">Title / Search:</div>
            </Col>
            <Col>
              <div className="custom-info-misc">{title}</div>
            </Col>
          </Row>

          <Row className="mt-2">
            <Col xs={5}>
              <div className="custom-info-title">Cost Basis (Total):</div>
            </Col>
            <Col>
              <div className="custom-info-val">
                <NumericFormat
                  value={totalCost || 0}
                  displayType={'text'}
                  thousandSeparator={true}
                  prefix={'$'}
                />
              </div>
            </Col>
          </Row>

          <Row>
            <Col xs={5}>
              <div className="custom-info-title">Raw Cost (Card Only):</div>
            </Col>
            <Col>
              <div className="custom-info-val">
                <NumericFormat
                  value={boughtPrice || 0}
                  displayType={'text'}
                  thousandSeparator={true}
                  prefix={'$'}
                />
              </div>
            </Col>
          </Row>

          {lastSoldPrice && (
            <Row>
              <Col xs={5}>
                <div className="custom-info-title">Last Sold Price (You):</div>
              </Col>
              <Col>
                <div className="custom-info-val">
                  <NumericFormat
                    value={lastSoldPrice}
                    displayType={'text'}
                    thousandSeparator={true}
                    prefix={'$'}
                  />
                </div>
              </Col>
            </Row>
          )}

          <hr />

          {/* Suggested list price logic */}
          <Row>
            <Col xs={5}>
              <div className="custom-info-title">Suggested List Range:</div>
            </Col>
            <Col>
              <div className="custom-info-val">
                {floorListPrice && suggestedListPrice ? (
                  <>
                    <NumericFormat
                      value={floorListPrice}
                      displayType={'text'}
                      thousandSeparator={true}
                      prefix={'$'}
                    />{' '}
                    –{' '}
                    <NumericFormat
                      value={suggestedListPrice}
                      displayType={'text'}
                      thousandSeparator={true}
                      prefix={'$'}
                    />
                  </>
                ) : (
                  'N/A'
                )}
              </div>
            </Col>
          </Row>

          <Row>
            <Col xs={5}>
              <div className="custom-info-title">Profit @ Suggested:</div>
            </Col>
            <Col>
              <div
                className={
                  profitAtSuggested && profitAtSuggested > 0
                    ? 'custom-info-val price-tracker-profit'
                    : 'custom-info-val'
                }
              >
                {profitAtSuggested ? (
                  <>
                    <NumericFormat
                      value={profitAtSuggested}
                      displayType={'text'}
                      thousandSeparator={true}
                      prefix={'$'}
                    />
                    {roiAtSuggested !== null && (
                      <span className="price-tracker-tag">
                        {roiAtSuggested}% ROI
                      </span>
                    )}
                  </>
                ) : (
                  'N/A'
                )}
              </div>
            </Col>
          </Row>

          <Row className="mt-3">
            <Col>
              <div className="custom-info-misc">
                Based on your stored costs plus live eBay sold data (below).
                Use this as a guide and still sanity-check against manual
                comps.
              </div>
            </Col>
          </Row>

          <hr />

          {/* Live market snapshot from backend / eBay */}
          <Row className="mt-2">
            <Col xs={12}>
              <div className="custom-info-header">Live eBay Sold Snapshot</div>
            </Col>
          </Row>

          {loadingMarket && (
            <Row className="mt-2">
              <Col>
                <div className="custom-info-misc">Loading eBay sold data…</div>
              </Col>
            </Row>
          )}

          {marketError && (
            <Row className="mt-2">
              <Col>
                <div className="custom-info-misc text-danger">
                  {marketError}
                </div>
              </Col>
            </Row>
          )}

          {!loadingMarket && !marketError && marketData?.ebay && (
            <>
              <Row className="mt-2">
                <Col xs={5}>
                  <div className="custom-info-title">eBay Avg Sold:</div>
                </Col>
                <Col>
                  <div className="custom-info-val">
                    <NumericFormat
                      value={marketData.ebay.avgSold}
                      displayType={'text'}
                      thousandSeparator={true}
                      prefix={'$'}
                    />
                    <span className="price-tracker-tag">
                      {marketData.ebay.soldCount} sales
                    </span>
                  </div>
                </Col>
              </Row>

              <Row>
                <Col xs={5}>
                  <div className="custom-info-title">Sold Range:</div>
                </Col>
                <Col>
                  <div className="custom-info-val">
                    <NumericFormat
                      value={marketData.ebay.minSold}
                      displayType={'text'}
                      thousandSeparator={true}
                      prefix={'$'}
                    />
                    {' – '}
                    <NumericFormat
                      value={marketData.ebay.maxSold}
                      displayType={'text'}
                      thousandSeparator={true}
                      prefix={'$'}
                    />
                  </div>
                </Col>
              </Row>

              {diffFromEbayAvg !== null && diffPctFromEbayAvg !== null && (
                <Row className="mt-2">
                  <Col xs={5}>
                    <div className="custom-info-title">
                      Vs. Your Total Cost:
                    </div>
                  </Col>
                  <Col>
                    <div className="custom-info-val">
                      <NumericFormat
                        value={Math.abs(diffFromEbayAvg)}
                        displayType={'text'}
                        thousandSeparator={true}
                        prefix={diffFromEbayAvg >= 0 ? '+$' : '-$'}
                      />
                      <span className="price-tracker-tag">
                        {diffFromEbayAvg >= 0 ? '+' : ''}
                        {diffPctFromEbayAvg}%
                      </span>
                    </div>
                  </Col>
                </Row>
              )}

              {marketData.lastUpdated && (
                <Row className="mt-1">
                  <Col>
                    <div className="custom-info-misc">
                      Last updated:{' '}
                      {new Date(marketData.lastUpdated).toLocaleString()}
                    </div>
                  </Col>
                </Row>
              )}
            </>
          )}

          <hr />

          {/* External research links */}
          <Row className="mt-2">
            <Col xs={12}>
              <div className="custom-info-header">Market Research Links</div>
            </Col>
          </Row>
          <Row className="mt-2">
            <Col xs={12}>
              <ul className="price-tracker-link-list">
                <li>
                  <a
                    href={ebaySearchUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    eBay – Active & Sold Listings
                  </a>
                </li>
                <li>
                  <a
                    href={tcgPlayerSearchUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    TCGPlayer – Market Prices
                  </a>
                </li>
                <li>
                  <a
                    href={point130SearchUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    130point – eBay Real Sold Data
                  </a>
                </li>
              </ul>
            </Col>
          </Row>
        </Col>
      </Row>
    </Container>
  );
};

export default PriceTracker;
